import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  brandAs,
  type ExamId,
  type IsoDateTime,
  type QuestionId,
  type SessionId,
} from '../core/model/brands.ts';
import type { CanonicalExam } from '../core/model/canonical.ts';
import type { AttemptHistory } from '../core/model/history.ts';
import type { Outcome } from '../core/model/session.ts';
import type { QuestionPatch } from '../core/export/reviewedExam.ts';
import {
  DEFAULT_PREFERENCES,
  loadPreferences,
  savePreferences,
  type Preferences,
} from '../persistence/preferences.ts';
import { clearHistory, loadHistory, recordSession } from '../persistence/historyStore.ts';
import {
  canResume,
  clearDraft,
  clearSavedSession,
  loadDraft,
  loadSavedSession,
  resumeSession,
  saveDraft,
  saveSession,
  type SavedSession,
  type SavedSessionDescriptor,
} from '../persistence/savedSession.ts';
import { clearAll } from '../persistence/store.ts';
import { createSession, type SessionState } from '../core/exam-engine/state.ts';
import { reduce, type Now, type SessionEvent } from '../core/exam-engine/reducer.ts';
import type { ValidationReport } from '../core/validator/report.ts';
import { ConfigScreen, type StartRequest } from '../features/configuration/ConfigScreen.tsx';
import { ImportScreen } from '../features/import/ImportScreen.tsx';
import { ResultsScreen, ReviewScreen } from '../features/results/ResultsScreen.tsx';
import { SessionScreen } from '../features/session/SessionScreen.tsx';
import { ValidationReportView } from '../features/validation-report/ValidationReportView.tsx';
import { useI18n } from '../i18n/index.tsx';
import { HeaderControls } from '../features/header/HeaderControls.tsx';
import { registerServiceWorker, type UpdateHandle } from '../pwa/serviceWorker.ts';
import { Button, Callout } from '../ui/primitives.tsx';
import { APP_VERSION } from './version.ts';

/**
 * Where the user is.
 *
 * Held in memory rather than in the URL. A deliberate limitation with two payoffs: no SPA fallback
 * to configure differently on each of the three hosting targets, and no link that looks resumable
 * but is not — the exam file lives on the user's device and cannot be recovered from an address.
 *
 * Note what is *not* here: there is no `results` route. Once a session exists, which screen shows is
 * derived from `session.status`, so "the exam is over" and "you are looking at your results" cannot
 * drift apart.
 */
type Route =
  | { readonly name: 'import' }
  | { readonly name: 'report'; readonly report: ValidationReport }
  | { readonly name: 'configure'; readonly exam: CanonicalExam; readonly report: ValidationReport }
  | { readonly name: 'session'; readonly exam: CanonicalExam };

/** Monotonic time, read in one place, so the reducer itself stays pure. */
const now = (): Now => ({
  monotonic: performance.now(),
  wall: brandAs<IsoDateTime>(new Date().toISOString()),
});

export function App() {
  const { t } = useI18n();
  const [route, setRoute] = useState<Route>({ name: 'import' });
  const [session, setSession] = useState<SessionState | null>(null);
  const [preferences, setPreferences] = useState(loadPreferences);
  const [history, setHistory] = useState<AttemptHistory>(loadHistory);
  const [saved, setSaved] = useState(loadSavedSession);
  /** The exam this tab was in the middle of, if a refresh interrupted one. */
  const [draft, setDraft] = useState(loadDraft);
  /**
   * The distraction-free view of the current question.
   *
   * Held here because its button belongs in the header, which is the shell's. It is a boolean and
   * nothing more: no mode, no policy, no second engine — the session underneath carries on exactly
   * as it was, which is why closing puts the candidate back where they were.
   */
  const [focus, setFocus] = useState(false);
  /**
   * A newer build is installed and waiting.
   *
   * Held rather than acted on: applying it reloads the page, and the page may have an exam in it.
   */
  const [update, setUpdate] = useState<UpdateHandle | null>(null);
  /** True while a question is actually on screen, so the header never offers a button that cannot act. */
  const [answering, setAnswering] = useState(false);

  // The text scale is a CSS variable on the root, so every size in the design system follows it —
  // rather than a font-size override that would leave the spacing around the text where it was.
  useEffect(() => {
    document.documentElement.style.setProperty('--text-scale', String(preferences.textScale / 100));
  }, [preferences.textScale]);

  /**
   * The chosen theme, as an attribute on the root.
   *
   * `system` removes the attribute rather than setting a third value: with nothing there, the
   * stylesheet's `color-scheme: light dark` is in charge, which is exactly "whatever the device
   * says" and is what the site did before there was a control.
   */
  useEffect(() => {
    const root = document.documentElement;
    if (preferences.theme === 'system') {
      delete root.dataset.theme;
    } else {
      root.dataset.theme = preferences.theme;
    }
  }, [preferences.theme]);

  const updatePreferences = useCallback((patch: Partial<Preferences>) => {
    setPreferences((current) => {
      const next = { ...current, ...patch };
      savePreferences(next);
      return next;
    });
  }, []);

  /** Turning memory off is a promise to forget, not merely to stop adding. */
  const setRemembering = useCallback(
    (value: boolean) => {
      updatePreferences({ rememberProgress: value });
      if (!value) {
        clearHistory();
        clearSavedSession();
        setHistory({});
        setSaved(null);
      }
    },
    [updatePreferences],
  );

  const forgetEverything = useCallback(() => {
    clearAll();
    setPreferences(DEFAULT_PREFERENCES);
    setHistory({});
    setSaved(null);
  }, []);

  useEffect(() => {
    registerServiceWorker(setUpdate);
  }, []);

  const goToImport = useCallback(() => {
    setSession(null);
    setFocus(false);
    setAnswering(false);
    setRoute({ name: 'import' });
    // Whatever is in the tab is the newest thing there is to offer.
    setDraft(loadDraft());
  }, []);

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        {t('app.skipToContent')}
      </a>

      <header className="app__header">
        <p className="app__brand">
          <BrandMark />
          {t('app.name')}
        </p>

        <HeaderControls
          textScale={preferences.textScale}
          onTextScale={(value) => {
            updatePreferences({ textScale: value });
          }}
          onLocale={(value) => {
            // Written where the choice is made rather than mirrored from i18n by an effect: a
            // preference is a thing someone did, not a value to keep two copies of in step.
            updatePreferences({ locale: value });
          }}
          theme={preferences.theme}
          onTheme={(value) => {
            updatePreferences({ theme: value });
          }}
          focusAvailable={answering}
          focusOn={focus}
          onToggleFocus={() => {
            setFocus((open) => !open);
          }}
        />
      </header>

      {/* Offered only between sessions. An update is a reload, and a reload during an exam is an
          hour of somebody's work — so the notice waits rather than interrupts. */}
      {update !== null && !answering && (
        <div className="update" role="status">
          <p className="update__text">{t('update.available')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              update.apply();
            }}
          >
            {t('update.apply')}
          </Button>
          <Button
            variant="quiet"
            onClick={() => {
              setUpdate(null);
            }}
          >
            {t('update.later')}
          </Button>
        </div>
      )}

      <main id="main" className="app__main" tabIndex={-1}>
        {route.name === 'import' && (
          <ImportScreen
            onLoaded={(exam, report) => {
              setRoute({ name: 'configure', exam, report });
            }}
            onRejected={(report) => {
              setRoute({ name: 'report', report });
            }}
          />
        )}

        {route.name === 'report' && (
          <ValidationReportView report={route.report} onRetry={goToImport} />
        )}

        {route.name === 'configure' && (
          <>
            <ExamHeader exam={route.exam} report={route.report} />

            <ResumeOffer
              exam={route.exam}
              draft={draft}
              saved={saved}
              onResume={(session) => {
                setSession(resumeSession(session, performance.now()));
                setAnswering(true);
                setRoute({ name: 'session', exam: route.exam });
              }}
              onDiscard={(source) => {
                if (source === 'draft') {
                  clearDraft();
                  setDraft(null);
                } else {
                  clearSavedSession();
                  setSaved(null);
                }
              }}
            />

            <ConfigScreen
              exam={route.exam}
              history={history[route.exam.meta.id] ?? {}}
              remembering={preferences.rememberProgress}
              onRemember={setRemembering}
              onForget={forgetEverything}
              onBack={goToImport}
              onStart={(request) => {
                setSession(startSession(route.exam, request));
                setAnswering(true);
                setRoute({ name: 'session', exam: route.exam });
              }}
            />
          </>
        )}

        {route.name === 'session' && session !== null && (
          <Running
            exam={route.exam}
            initial={session}
            remembering={preferences.rememberProgress}
            onRecord={(examId, outcomes, at) => {
              recordSession(examId, outcomes, at);
              setHistory(loadHistory());
              // A finished session is not a session to resume.
              clearSavedSession();
              setSaved(null);
            }}
            onSaved={() => {
              setSaved(loadSavedSession());
            }}
            focus={focus}
            onExitFocus={() => {
              setFocus(false);
            }}
            onFinished={() => {
              setFocus(false);
              setAnswering(false);
            }}
            onRestart={goToImport}
          />
        )}
      </main>

      <footer className="app__footer">
        <p>
          <a href="https://freexamplayer.com/schema/" rel="noopener noreferrer">
            {t('import.formatHelp')}
          </a>
          <span aria-hidden="true"> · </span>
          <span>FreExam Player {APP_VERSION}</span>
        </p>
      </footer>
    </div>
  );
}

/**
 * The offer to pick up where a session left off.
 *
 * Two sources, and they mean different things to the person reading. A **draft** is the exam this
 * tab was in the middle of when something interrupted it — a refresh, a crash — and nobody asked
 * for it to be kept. A **saved session** was saved on purpose and survives closing the tab. The
 * draft wins when both fit, because it is the more recent of the two by construction.
 *
 * Either way the exam file has to be chosen again and its content hash has to match, which is what
 * stops a session being continued against a file that has changed underneath it.
 */
function ResumeOffer({
  exam,
  draft,
  saved,
  onResume,
  onDiscard,
}: {
  readonly exam: CanonicalExam;
  readonly draft: SavedSession | null;
  readonly saved: SavedSession | null;
  readonly onResume: (session: SavedSession) => void;
  readonly onDiscard: (source: 'draft' | 'saved') => void;
}) {
  const { t } = useI18n();

  const offer =
    draft !== null && canResume(draft, exam) === 'none'
      ? ({ source: 'draft', session: draft } as const)
      : saved !== null && canResume(saved, exam) === 'none'
        ? ({ source: 'saved', session: saved } as const)
        : null;

  if (offer === null) {
    return null;
  }

  return (
    <Callout
      tone="info"
      title={offer.source === 'draft' ? t('resume.draftTitle') : t('resume.title')}
    >
      <p>
        {t('resume.detail', {
          answered: answeredCount(offer.session.state),
          total: offer.session.state.blueprint.questionOrder.length,
        })}
      </p>
      <div className="actions">
        <Button
          variant="primary"
          onClick={() => {
            onResume(offer.session);
          }}
        >
          {t('resume.continue')}
        </Button>
        <Button
          onClick={() => {
            onDiscard(offer.source);
          }}
        >
          {t('resume.discard')}
        </Button>
      </div>
    </Callout>
  );
}

/** How much of a saved session was already done, for the sentence that offers to continue it. */
function answeredCount(state: SessionState): number {
  return state.blueprint.questionOrder.filter((id) => state.entries[id]?.response != null).length;
}

function startSession(exam: CanonicalExam, request: StartRequest): SessionState {
  return createSession({
    id: brandAs<SessionId>(crypto.randomUUID()),
    candidateName: request.candidateName,
    exam,
    blueprint: request.blueprint,
    policy: request.policy,
    criteria: request.criteria,
    modePreset: request.modePreset,
    startedAt: brandAs<IsoDateTime>(new Date().toISOString()),
    monotonicNow: performance.now(),
  });
}

/**
 * A live session and everything that follows it.
 *
 * One reducer for the whole lifecycle — running, submitted, under review — because they are one
 * state machine. Which screen appears is read off `status`; nothing navigates.
 */
function Running({
  exam,
  initial,
  remembering,
  onRecord,
  onSaved,
  onFinished,
  onRestart,
  focus,
  onExitFocus,
}: {
  readonly exam: CanonicalExam;
  readonly initial: SessionState;
  readonly remembering: boolean;
  readonly onRecord: (
    examId: ExamId,
    outcomes: readonly { readonly questionId: QuestionId; readonly outcome: Outcome }[],
    at: IsoDateTime,
  ) => void;
  readonly onSaved: () => void;
  /** Called once when the session stops being answerable, so the shell can retire its controls. */
  readonly onFinished: () => void;
  readonly onRestart: () => void;
  readonly focus: boolean;
  readonly onExitFocus: () => void;
}) {
  const [state, dispatch] = useReducer(
    (current: SessionState, event: SessionEvent) => reduce(current, event, now(), exam),
    initial,
  );
  const recorded = useRef(false);
  /** Which question review opens on: the top, unless a note sent us to one. */
  const [reviewStart, setReviewStart] = useState(0);
  /**
   * Corrections the candidate made while reviewing.
   *
   * Not session state: a correction is about the **exam**, not about the attempt, and the engine
   * has no business knowing that somebody disagrees with an answer key. It lives here, next to the
   * screen that collects it and the screen that exports it.
   */
  const [patches, setPatches] = useState<Record<string, QuestionPatch>>({});

  /**
   * What happens once, when the session ends.
   *
   * In an effect because both of these are side effects on the world outside the reducer: the
   * store gets written, and the shell is told to put away the controls that only make sense while
   * a question is on screen. The ref is what makes "once" true — this runs again on every render
   * that follows submission, including entering and leaving review.
   */
  useEffect(() => {
    if (recorded.current || state.status !== 'completed') {
      return;
    }
    recorded.current = true;
    // A finished session is not a draft to come back to.
    clearDraft();
    onFinished();

    if (!remembering) {
      return;
    }
    onRecord(
      state.examRef.examId,
      state.blueprint.questionOrder.map((questionId) => ({
        questionId,
        outcome: state.scores[questionId]?.outcome ?? 'unanswered',
      })),
      state.submittedAt ?? brandAs<IsoDateTime>(new Date().toISOString()),
    );
  }, [remembering, state, onRecord, onFinished]);

  const describe = useCallback(
    (): SavedSessionDescriptor => ({
      examId: state.examRef.examId,
      contentHash: state.examRef.contentHash,
      title: state.examRef.title,
      savedAt: new Date().toISOString(),
      answered: answeredCount(state),
      total: state.blueprint.questionOrder.length,
    }),
    [state],
  );

  const save = useCallback((): boolean => {
    const ok = saveSession(state, describe());
    if (ok) {
      onSaved();
    }
    return ok;
  }, [state, describe, onSaved]);

  /**
   * Keeps a draft of the session in the tab, without being asked.
   *
   * Losing an hour of work to a stray refresh is the worst thing this application could do to
   * anyone, and the warning before leaving is only a warning. The draft goes to `sessionStorage`,
   * so it survives the refresh and dies with the tab — the promise that closing the tab discards
   * the session stays exactly as true as it was.
   *
   * Written on a short delay rather than on every state change: a note is typed one keystroke at a
   * time, and serialising the whole session on each of them would be work nobody asked for. The
   * `pagehide` handler is the one that actually catches a refresh — it runs synchronously, while
   * the page is being torn down, which is precisely when a debounced write would be too late.
   */
  useEffect(() => {
    if (state.status === 'completed' || state.status === 'reviewing') {
      return;
    }

    const persist = (): void => {
      saveDraft(state, describe());
    };
    const handle = window.setTimeout(persist, 1000);
    window.addEventListener('pagehide', persist);

    return () => {
      window.clearTimeout(handle);
      window.removeEventListener('pagehide', persist);
    };
  }, [state, describe]);

  if (state.status === 'reviewing') {
    return (
      <ReviewScreen
        exam={exam}
        session={state}
        startAt={reviewStart}
        patches={patches}
        onPatch={(questionId, patch) => {
          setPatches((current) =>
            patch === undefined
              ? Object.fromEntries(Object.entries(current).filter(([id]) => id !== questionId))
              : { ...current, [questionId]: patch },
          );
        }}
        onDispatch={dispatch}
        onBack={() => {
          dispatch({ type: 'EXIT_REVIEW' });
        }}
      />
    );
  }

  if (state.status === 'completed') {
    return (
      <ResultsScreen
        exam={exam}
        session={state}
        patch={{ questions: Object.values(patches) }}
        onRestart={onRestart}
        onReview={() => {
          setReviewStart(0);
          dispatch({ type: 'ENTER_REVIEW' });
        }}
        onGoToQuestion={(index) => {
          setReviewStart(index);
          dispatch({ type: 'ENTER_REVIEW' });
        }}
      />
    );
  }

  return (
    <SessionScreen
      exam={exam}
      state={state}
      dispatch={dispatch}
      onAbandon={onRestart}
      onSave={remembering ? save : undefined}
      focus={focus}
      onExitFocus={onExitFocus}
    />
  );
}

/**
 * The wordmark.
 *
 * Inline SVG rather than an image file: it inherits the text colour, needs no request, and cannot
 * be the thing that fails to load offline. Ours, not imported content, so there is no untrusted
 * markup here.
 */
function BrandMark() {
  return (
    <svg className="brand-mark" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M7.5 12.4 10.6 15.5 16.5 9"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** What was loaded, and anything worth knowing about it before starting. */
function ExamHeader({
  exam,
  report,
}: {
  readonly exam: CanonicalExam;
  readonly report: ValidationReport;
}) {
  const { t } = useI18n();
  const [showReport, setShowReport] = useState(false);
  const notes = report.counts.warning + report.counts.info;

  if (showReport) {
    return (
      <ValidationReportView
        report={report}
        onRetry={() => {
          setShowReport(false);
        }}
      />
    );
  }

  return (
    <section className="exam-header">
      <h2>{exam.meta.title}</h2>
      <p className="exam-header__meta">
        {t('exam.questionCount', { count: exam.questions.length })}
        {' · '}
        {exam.meta.license === undefined
          ? t('exam.licenseUnknown')
          : t('exam.license', { license: exam.meta.license })}
        {exam.meta.source?.title !== undefined &&
          ` · ${t('exam.source', { source: exam.meta.source.title })}`}
      </p>

      {notes > 0 && (
        <Callout tone="warning">
          <p>{t('exam.warnings', { count: notes })}</p>
          <Button
            variant="quiet"
            onClick={() => {
              setShowReport(true);
            }}
          >
            {t('exam.viewReport')}
          </Button>
        </Callout>
      )}
    </section>
  );
}
